/**
 * Native-mode bridge — runs only inside the Capacitor Android app.
 * Silently no-ops in the browser / PWA. Attach to <App /> once at boot.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

export function useNativeBridge() {
  const nav = useNavigate();

  useEffect(() => {
    let cleanup = () => {};
    (async () => {
      try {
        const cap = await import("@capacitor/core").catch(() => null);
        if (!cap || !cap.Capacitor?.isNativePlatform?.()) return;

        const [StatusBarMod, SplashMod, AppMod] = await Promise.all([
          import("@capacitor/status-bar").catch(() => null),
          import("@capacitor/splash-screen").catch(() => null),
          import("@capacitor/app").catch(() => null),
        ]);

        // Status bar — amber, dark icons
        if (StatusBarMod?.StatusBar) {
          try {
            await StatusBarMod.StatusBar.setStyle({ style: "DARK" });
            await StatusBarMod.StatusBar.setBackgroundColor({ color: "#F59E0B" });
          } catch { /* Ignore */ }
        }

        // Hide splash after first paint
        if (SplashMod?.SplashScreen) {
          setTimeout(() => SplashMod.SplashScreen.hide().catch(() => {}), 600);
        }

        // Android hardware back button → history back, or exit at root
        if (AppMod?.App) {
          const sub = await AppMod.App.addListener("backButton", ({ canGoBack }) => {
            if (canGoBack) {
              window.history.back();
            } else if (["/", "/login", "/csa"].includes(window.location.pathname)) {
              AppMod.App.exitApp();
            } else {
              nav("/", { replace: true });
            }
          });

          // Deep-link routing — App Shortcuts fire hisabx://csa?tab=online etc.
          // Route them to the correct in-app URL when the intent lands.
          const handleUrl = ({ url }) => {
            if (!url) return;
            try {
              const parsed = new URL(url);
              if (parsed.protocol !== "hisabx:") return;
              // hisabx://csa?tab=online → path = "//csa" (URL polyfill quirk)
              // Normalize: everything after the scheme is the destination
              const dest = url.replace(/^hisabx:\/\//, "/");
              nav(dest, { replace: false });
            } catch { /* Ignore malformed URLs */ }
          };
          const urlSub = await AppMod.App.addListener("appUrlOpen", handleUrl);

          // Cold launch — if the app was started FROM a shortcut, the URL
          // isn't fired via the listener. We fetch it via getLaunchUrl().
          try {
            const launch = await AppMod.App.getLaunchUrl();
            if (launch?.url) handleUrl(launch);
          } catch { /* Ignore — not all Capacitor versions expose this */ }

          const prevCleanup = cleanup;
          cleanup = () => { prevCleanup(); sub.remove?.(); urlSub.remove?.(); };
        }

        // Expose a flag so components can style differently on native
        document.body.classList.add("hisabx-native");
      } catch (e) {
        // Native bridge is optional — never crash the app
        console.warn("Native bridge init failed", e);
      }
    })();
    return () => cleanup();
  }, [nav]);
}
